"use client"
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Clock, MapPin } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import Section from '@/components/layout/Section';
import ComingSoonButton from '@/components/ui/coming-soon-button';

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const heroImages = [
    '/food1.jpg',  // Replace with your actual mochi images
    '/food2.jpg',
    '/food3.jpg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="relative w-full min-h-[100vh] bg-neutral-900 overflow-hidden">
      {/* Background Image Layer */}
      <div className="absolute inset-0 w-full h-full">
        {heroImages.map((image, index) => (
          <motion.div
            key={image}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: currentImageIndex === index ? 1 : 0,
              scale: currentImageIndex === index ? 1 : 1.1,
            }}
            transition={{
              opacity: { duration: 1.2, ease: "easeInOut" },
              scale: { duration: 6, ease: "easeInOut" }
            }}
          >
            <Image
              src={image}
              alt={`Mochi Mochi Chillin showcase image ${index + 1}`}
              fill
              className="object-cover object-center"
              priority={index === 0}
              quality={95}
            />
            <div className="absolute inset-0">
              {/* Base gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/30 via-neutral-900/20 to-neutral-900/40" />
              
              {/* Warm color tint */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-orange/5 via-transparent to-[#7AC74F]/5 mix-blend-overlay" />
              
              {/* Edge darkening */}
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/20 via-transparent to-transparent" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Floating Decorative Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Animated Circles */}
        <motion.div
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            rotate: [0, 10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 right-20 w-32 h-32 rounded-full bg-primary-orange/20 blur-2xl"
        />
        <motion.div
          animate={{
            y: [20, -20, 20],
            x: [10, -10, 10],
            rotate: [0, -10, 0]
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-20 left-20 w-40 h-40 rounded-full bg-[#7AC74F]/20 blur-2xl"
        />

        {/* Playful Shapes */}
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-1/4 right-1/3 w-16 h-16"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L37.3205 22.6795L60 30L37.3205 37.3205L30 60L22.6795 37.3205L0 30L22.6795 22.6795L30 0Z' fill='rgba(255, 107, 53, 0.2)'/%3E%3C/svg%3E\")",
            backgroundSize: 'contain'
          }}
        />
      </div>

      {/* Content Container */}
      <div className="relative h-screen max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col h-full pt-28">
          {/* Main Content Area */}
          <div className="flex-1 grid lg:grid-cols-[1.2fr_auto] gap-8 lg:gap-16 items-center">
            {/* Left Side - Main Content */}
            <div className="max-w-xl lg:max-w-2xl xl:max-w-3xl">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-bold mb-5"
              >
                <motion.span 
                  className="inline-block"
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  Experience
                </motion.span>
                <span className="block mt-1.5">
                  <motion.span 
                    className="text-primary-orange inline-block"
                    whileHover={{ scale: 1.05, rotate: 2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    Artisanal
                  </motion.span>
                  {" "}
                  <motion.span 
                    className="inline-block"
                    whileHover={{ scale: 1.05, rotate: -2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    Mochi
                  </motion.span>
                </span>
                <motion.span 
                  className="block mt-1.5"
                  whileHover={{ scale: 1.05, rotate: 1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  Donuts & Coffee
                </motion.span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-base md:text-lg lg:text-xl text-white/80 max-w-xl mb-8"
              >
                Discover our handcrafted Japanese-inspired mochi donuts, made fresh daily with premium ingredients. A perfect blend of chewy mochi texture and creative flavors.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-wrap gap-4"
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ComingSoonButton 
                    className="bg-primary-orange hover:bg-primary-orange/90 text-white font-heading text-[15px] px-7 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                    aria-label="Order Now - Coming Soon"
                  >
                    Order Now
                  </ComingSoonButton>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link href="/menu" aria-label="View our full menu">
                    <Button className="bg-white/10 hover:bg-white/20 text-white font-heading text-[15px] px-7 py-3.5 rounded-full backdrop-blur-md border border-white/20 transition-all duration-300">
                      Explore Menu
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>

            {/* Right Side - Gallery and Info */}
            <div className="hidden lg:flex flex-col gap-5 w-[450px]">
              {/* Blob Gallery with Info Overlay */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="relative w-full aspect-[4/5]"
              >
                {/* Blob Shape Container */}
                <div className="absolute inset-0">
                  <svg
                    viewBox="0 0 500 500"
                    className="w-full h-full transform scale-110"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M423.1 141.4c21.8 36.3 28.2 81.6 21.1 124.4-7 42.8-27.6 83.1-58.5 117.1-30.9 34-72 61.7-116.3 63.9-44.3 2.2-91.8-21.1-131.2-56.3-39.4-35.2-70.7-82.3-75.4-134.2-4.7-51.9 17.2-108.5 55.2-147.1 38-38.6 92.1-59.2 145.5-57.9 53.4 1.3 106 24.5 138.5 51.8 32.4 27.3 42.8 58.7 21.1 38.3z"
                      className="fill-white/5 backdrop-blur-xl"
                    />
                  </svg>
                </div>

                {/* Storefront Image */}
                <div className="absolute inset-4 rounded-3xl overflow-hidden">
                  <Image
                    src="/storefront.jpg"
                    alt="Mochi Mochi Chillin Storefront"
                    fill
                    className="object-cover"
                    sizes="450px"
                    priority
                    quality={90}
                  />
                </div>

                {/* Info Overlay */}
                <div className="absolute inset-4 rounded-3xl bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-6 flex gap-4">
                    {/* Location Info */}
                    <div className="flex-1 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <MapPin className="w-4 h-4 text-primary-orange" />
                        <p className="text-white font-medium text-sm">Visit Us</p>
                      </div>
                      <p className="text-white/90 text-sm mb-1.5">28910 Hardin Store Rd, Suite 102</p>
                      <Link 
                        href="https://maps.google.com/?q=28910+Hardin+Store+Rd+suite+102,+Magnolia,+TX+77354"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-orange hover:text-primary-light text-sm font-medium transition-colors duration-300 inline-flex items-center gap-1"
                      >
                        Get Directions 
                        <motion.span
                          animate={{ x: [0, 3, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          →
                        </motion.span>
                      </Link>
                    </div>

                    {/* Hours Info */}
                    <div className="flex-1 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Clock className="w-4 h-4 text-primary-orange" />
                        <p className="text-white font-medium text-sm">Hours</p>
                      </div>
                      <p className="text-white/90 text-sm">
                        Mon-Thu: 10AM–9PM<br />
                        Fri-Sat: 10AM–10PM<br />
                        Sun: 11AM–8PM
                      </p>
                    </div>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-primary-orange/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-[#7AC74F]/20 rounded-full blur-3xl" />
              </motion.div>
            </div>
          </div>

          {/* Mobile Bottom Info (Only visible on mobile) */}
          <div className="lg:hidden mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="grid gap-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-4"
            >
              {/* Mobile Visit Us */}
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-primary-orange flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white/90 font-medium mb-1">Visit Us</p>
                  <p className="text-white/70 text-sm">28910 Hardin Store Rd suite 102, Magnolia, TX 77354</p>
                </div>
              </div>

              {/* Mobile Hours */}
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-primary-orange flex-shrink-0 mt-1" />
                <div>
                  <p className="text-white/90 font-medium mb-1">Hours</p>
                  <p className="text-white/70 text-sm">Mon-Thu: 10AM–9PM • Fri-Sat: 10AM–10PM • Sun: 11AM–8PM</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;